import useTabs from 'src/components/10-atoms/tabs/useTabs';
import usePersistedState from 'src/hooks/globalState/usePersistedState';
import useEffectWithCtx from 'src/hooks/useEffectUtils/useEffectWithCtx';
import StorageKeys from 'src/lib/storage/storageKeys';
import useIsSuperAdmin from 'src/modules/authentication/hooks/useIsSuperAdmin';
import { useRPPendingCountQuery } from 'src/modules/review-pipeline/graphql/pendingCount';
import { SupportedRPEntityTypes } from 'src/modules/review-pipeline/graphql/utils';
import useI18n from 'src/modules/translations/hooks/useI18n';
import isNot from 'src/utils/array/isNot';
import { useCommunityDetailsQuery } from '../../graphql/community/getDetails';
import { useExamLevelDetailsQuery } from '../../graphql/exam-level/getDetails';
export default function useData() {
    const [selectedExamLevel, setSelectedExamLevel] = usePersistedState(StorageKeys.epSelectedExamLevel);
    const { translate } = useI18n();
    const [communityDetailsQueryState, communityDetailsQueryActions] = useCommunityDetailsQuery({
        lazy: true,
    });
    useEffectWithCtx(ctx => {
        if (communityDetailsQueryState.data) {
            if (communityDetailsQueryState.data.ep_communityDetails.id ===
                ctx.selectedExamLevel?.community.id) {
                ctx.setSelectedExamLevel({
                    ...ctx.selectedExamLevel,
                    community: communityDetailsQueryState.data.ep_communityDetails,
                });
            }
        }
    }, { selectedExamLevel, setSelectedExamLevel }, [communityDetailsQueryState.data]);
    const [examLevelDetailsQueryState, examLevelDetailsQueryActions] = useExamLevelDetailsQuery({
        lazy: true,
    });
    useEffectWithCtx(ctx => {
        if (examLevelDetailsQueryState.data) {
            if (examLevelDetailsQueryState.data.ep_examLevelDetails.id ===
                ctx.selectedExamLevel?.examLevel?.id) {
                ctx.setSelectedExamLevel({
                    ...ctx.selectedExamLevel,
                    examLevel: examLevelDetailsQueryState.data.ep_examLevelDetails,
                });
            }
        }
    }, { selectedExamLevel, setSelectedExamLevel }, [examLevelDetailsQueryState.data]);
    useEffectWithCtx(ctx => {
        if (selectedExamLevel?.community &&
            selectedExamLevel.community.id !==
                communityDetailsQueryState.data?.ep_communityDetails.id) {
            ctx.communityDetailsQueryActions.fetch({
                communityID: selectedExamLevel.community.id,
            });
        }
    }, { communityDetailsQueryActions }, [selectedExamLevel?.community, communityDetailsQueryState.data]);
    useEffectWithCtx(ctx => {
        if (selectedExamLevel?.examLevel &&
            selectedExamLevel.examLevel.id !==
                examLevelDetailsQueryState.data?.ep_examLevelDetails.id) {
            ctx.examLevelDetailsQueryActions.fetch({
                examLevelID: selectedExamLevel.examLevel.id,
            });
        }
    }, { examLevelDetailsQueryActions }, [selectedExamLevel?.examLevel, examLevelDetailsQueryState.data]);
    const [tabsState, tabsActions] = useTabs();
    useEffectWithCtx(ctx => {
        if (selectedExamLevel?.examLevel) {
            const config = selectedExamLevel?.examLevel.config;
            const shouldShowWritten = config?.EXAM_LEVEL?.written?.enable ?? true;
            const shouldShowOrals = config?.EXAM_LEVEL?.orals?.enable ?? false;
            ctx.tabsActions.setTabs([
                shouldShowWritten && {
                    id: 'WRITTEN',
                    label: translate('Written'),
                },
                shouldShowOrals && {
                    id: 'ORALS',
                    label: translate('Orals'),
                },
            ].filter(isNot(false)));
            ctx.tabsActions.setActiveIndex(0);
        }
    }, { tabsActions }, [selectedExamLevel]);
    const isSuperAdmin = useIsSuperAdmin();
    const [reviewPendingCountQueryState, reviewPendingCountQueryActions] = useRPPendingCountQuery({
        skip: !isSuperAdmin,
        variables: {
            types: SupportedRPEntityTypes,
        },
        pollInterval: 5 * 60000,
    });
    const state = {
        isSuperAdmin,
        reviewPendingCountQuery: reviewPendingCountQueryState,
        selectedExamLevel,
        tabs: tabsState,
    };
    const actions = {
        reviewPendingCountQuery: reviewPendingCountQueryActions,
        setSelectedExamLevel,
        tabs: tabsActions,
    };
    return [state, actions];
}
